import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { Select } from "@/components/select";
import { useQueryParameters } from "@/hooks/useQueryParameters";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { PaginationComponent } from "@/components/PaginationComponent";
import {
  useQueryGetExamAssignmentList,
  ExamAssignmentStatus,
  type IExamAssignmentListParams,
  type IExamAssignmentListResponse,
} from "@/apis/exam_assignments";
import { getPaginationInfo } from "@/types/pagination";
import type { IPagination } from "@/types/pagination";
import { Badge } from "@/components/badge";

const ExamAssignmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");

  // Configure query parameters
  const { params, setParam, setParams } = useQueryParameters({
    page: {
      defaultValue: 1,
      removeWhenDefault: true,
    },
    search: {
      defaultValue: "",
      removeWhenDefault: true,
    },
    limit: {
      defaultValue: 10,
      removeWhenDefault: true,
    },
  }, {
    storageKey: "examAssignmentListSettings",
  });

  // Sync search input with URL param
  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  // API 호출
  const { data, isLoading, error } = useQueryGetExamAssignmentList({
    page: params.page,
    limit: params.limit,
    title: params.search || undefined,
  }) as {
    data: IPagination<IExamAssignmentListResponse> | null;
    isLoading: boolean;
    error: any;
  };

  // 페이지네이션 정보
  const paginationInfo =
    data && Array.isArray(data.items) ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setParam("page", page);
  };

  // 검색 핸들러
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParams({
      search: searchInput,
      page: 1,
    });
  };

  // 시험 할당 페이지로 이동
  const handleAddExamAssignment = () => {
    navigate("/exam_assignments/create");
  };

  // 상세 페이지로 이동
  const handleRowClick = (id: string) => {
    navigate(`/exam_assignments/${id}`);
  };

  // 상태에 따른 배지 표시
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case ExamAssignmentStatus.ACTIVE:
        return <Badge color="green">Active</Badge>;
      case ExamAssignmentStatus.CANCELLED:
        return <Badge color="red">Cancelled</Badge>;
      default:
        return <Badge color="zinc">{status}</Badge>;
    }
  };

  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // 할당된 학생 수 표시
  const getStudentCount = (targetUsers?: { user_id: string }[]) => {
    return Array.isArray(targetUsers) ? targetUsers.length : 0;
  };

  // 과목 배지 표시
  const renderSubjectBadges = (examSet?: any) => {
    if (!examSet?.items || !Array.isArray(examSet.items)) {
      return <span className="text-gray-500">-</span>;
    }

    if (examSet.items.length === 0) {
      return <span className="text-gray-500">-</span>;
    }

    return (
      <div className="flex flex-col gap-1">
        {examSet.items.map((item: any, index: number) => (
          <div key={`${item.subject}-${index}`} className="flex items-center gap-2">
            <Badge color="zinc" className="text-xs">
              {item.subject === 'reading_writing' ? 'R&W' : 
               item.subject === 'math' ? 'Math' : 
               item.subject ? item.subject.charAt(0).toUpperCase() + item.subject.slice(1) : 'Unknown'}
            </Badge>
            <span className="text-xs text-gray-500">
              {item.is_private ? 'Private' : 'Public'}
            </span>
          </div>
        ))}
      </div>
    );
  };


  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Exam Assignments</Heading>
        <Text className="text-zinc-500">
          Manage bulk exam assignments to students.
        </Text>

        <div className="flex justify-between items-center mt-4">
          <Button onClick={handleAddExamAssignment}>
            <PlusIcon className="w-5 h-5 mr-1" />
            Assign Exam
          </Button>

          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                name="search"
                placeholder="Exam name..."
                aria-label="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {/* 로딩 및 오류 처리 */}
        {isLoading && (
          <div className="text-center py-4">Loading exam assignments...</div>
        )}
        {error && (
          <div className="text-center py-4 text-red-500">
            Error loading exam assignments. Please try again.
          </div>
        )}

        {/* 시험 할당 테이블 */}
        {!isLoading && !error && (
          <Table className="mt-4">
            <TableHead>
              <TableRow>
                <TableHeader>Title</TableHeader>
                <TableHeader>Subjects</TableHeader>
                <TableHeader>Students</TableHeader>
                <TableHeader>Created</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {data && Array.isArray(data.items) && data.items.length > 0 ? (
                data.items.map((assignment: IExamAssignmentListResponse) => (
                  <TableRow
                    key={assignment.id}
                    onClick={() => handleRowClick(assignment.id)}
                    className="cursor-pointer hover:bg-zinc-50"
                  >
                    <TableCell>
                      <div className="font-medium text-gray-900">{assignment.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {assignment.exam_set?.exam_type || "-"}
                      </div>
                    </TableCell>
                    <TableCell>{renderSubjectBadges(assignment.exam_set)}</TableCell>
                    <TableCell>
                      {getStudentCount(assignment.target_users)} students
                    </TableCell>
                    <TableCell>{formatDate(assignment.created_at)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-4">
                    No exam assignments found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default ExamAssignmentListPage;
